import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { Send, Remove, Get } from '../../services/service.api';
import { toast } from 'react-toastify';
import { Plus, Trash2, X, Calendar, RotateCcw, Search } from 'lucide-react';
import ConfirmModal from '../../components/component.confirmModal';
import FullScreenLoader from '../../components/FullScreenLoader';

function formatFCFA(n) { return `${Number(n || 0).toLocaleString('fr-FR')} FCFA`; }

function InvestmentModal({ onClose, onSave, t, token, activeCompany }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  const [form, setForm] = useState({
    campagne_id: '',
    montant: '',
    notes: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    setLoadingData(true);
    Get(`/campaigns?_t=${Date.now()}`, token)
      .then(res => { if (Array.isArray(res)) setCampaigns(res); })
      .catch(() => {})
      .finally(() => setLoadingData(false));
  }, [token, activeCompany]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.campagne_id || !form.montant) return toast.error('Campagne et montant obligatoires');
    onSave(form);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">{t.investments.add}</h2>
          <button className="modal-close" onClick={onClose}><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">{t.investments.campagne} <span className="required">*</span></label>
              {loadingData ? (
                <div className="skeleton" style={{ height: 38, borderRadius: 'var(--radius-sm)' }} />
              ) : (
                <select className="form-select" value={form.campagne_id} onChange={e => setForm({ ...form, campagne_id: e.target.value })} required>
                  <option value="">-- Sélectionner une campagne --</option>
                  {campaigns.map(c => <option key={c.id} value={c.id}>{c.libelle}</option>)}
                </select>
              )}
            </div>
            <div className="form-group">
              <label className="form-label">{t.investments.montant} <span className="required">*</span></label>
              <input type="number" min="1" className="form-control" value={form.montant} required
                onChange={e => setForm({ ...form, montant: e.target.value })} placeholder="0" />
            </div>
            <div className="form-group">
              <label className="form-label">{t.investments.date}</label>
              <input
                type="date" className="form-control"
                value={form.date}
                onChange={e => setForm({ ...form, date: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="form-label">{t.investments.notes}</label>
              <textarea className="form-control" value={form.notes} rows={2}
                onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Notes optionnelles..." />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>{t.common.cancel}</button>
            <button type="submit" className="btn btn-primary">{t.common.save}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Investments() {
  const { t } = useTranslation();
  const token = useSelector(s => s.userReducer.token);
  const user = useSelector(s => s.userReducer.user);
  const activeCompany = useSelector(s => s.userReducer.activeCompany);
  const isInvestor = user?.role === 'investisseur';
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  // Filters
  const [filterDateDebut, setFilterDateDebut] = useState('');
  const [filterDateFin, setFilterDateFin] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  const abortControllerRef = useRef(null);

  const fetchInvestments = useCallback(async () => {
    if (abortControllerRef.current) abortControllerRef.current.abort();
    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setLoading(true);
    try {
      const companyId = activeCompany?.id || '';
      const headers = { authorization: "Bearer " + token, 'Content-Type': 'application/json' };
      if (companyId) headers['X-Company-Id'] = companyId;

      const params = new URLSearchParams();
      if (filterDateDebut) params.append('date_debut', filterDateDebut);
      if (filterDateFin) params.append('date_fin', filterDateFin);
      if (companyId) params.append('company_id', companyId);
      params.append('_t', Date.now());
      const qs = params.toString() ? `?${params.toString()}` : '';

      const API_URL = process.env.REACT_APP_API_URL || "https://savaninvests-backend.onrender.com";
      const response = await fetch(`${API_URL}/investments${qs}`, { headers, signal });
      const res = await response.json();

      if (!signal.aborted && Array.isArray(res)) {
        setInvestments(res);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        // silently fail
      }
    } finally {
      if (!signal.aborted) setLoading(false);
    }
  }, [token, filterDateDebut, filterDateFin, activeCompany?.id]);

  useEffect(() => {
    setInvestments([]); // Clear to prevent ghost data
    fetchInvestments();
  }, [fetchInvestments]);

  useEffect(() => {
    const handleSwitched = () => fetchInvestments();
    window.addEventListener('company-switched', handleSwitched);
    return () => window.removeEventListener('company-switched', handleSwitched);
  }, [fetchInvestments]);

  const handleSave = async (form) => {
    try {
      const res = await Send('/investments', form, true, token);
      if (res?.error) return toast.error(res.error);
      toast.success('Investissement enregistré ✓');
      setShowModal(false);
      fetchInvestments();
    } catch { toast.error('Erreur'); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await Remove(`/investments/${deleteId}`, token);
      toast.success('Investissement supprimé');
      setDeleteId(null);
      fetchInvestments();
    } catch { toast.error('Erreur'); }
  };

  const resetFilters = () => {
    setFilterDateDebut('');
    setFilterDateFin('');
    setSearchQuery('');
  };

  const isFilterActive = filterDateDebut !== '' || filterDateFin !== '' || searchQuery !== '';

  const filteredInvestments = investments.filter(inv => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      (inv.campagne_libelle && String(inv.campagne_libelle).toLowerCase().includes(q)) ||
      (inv.notes && String(inv.notes).toLowerCase().includes(q))
    );
  });

  const totalFCFA = filteredInvestments.reduce((acc, i) => acc + Number(i.montant || 0), 0);

  return (
    <div className="page-content page-enter">
      {loading && <FullScreenLoader message="Chargement des investissements..." />}
      <div className="page-header">
        <div>
          <h1>{t.investments.title}</h1>
          <p>Total investi : <strong style={{ color: 'var(--gold)' }} className="number-format">{formatFCFA(totalFCFA)}</strong> ({filteredInvestments.length} investissements)</p>
        </div>
        {isInvestor && (
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <Plus size={16} /> {t.investments.add}
          </button>
        )}
      </div>

      {/* Filter Bar */}
      <div className="dashboard-filter-bar mb-4">
        <div className="filter-group-wrapper">
          <div className="search-wrapper" style={{ minWidth: 200, maxWidth: "100%" }}>
            <Search size={14} />
            <input
              type="text"
              className="form-control search-input"
              placeholder="Rechercher notes..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{ fontSize: '0.85rem' }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar size={14} style={{ color: 'var(--gold)' }} />
            <input
              type="date"
              className="filter-input-date"
              value={filterDateDebut}
              onChange={e => setFilterDateDebut(e.target.value)}
              title="Date début"
            />
            <span style={{ color: 'var(--text-muted)' }}>→</span>
            <input
              type="date"
              className="filter-input-date"
              value={filterDateFin}
              onChange={e => setFilterDateFin(e.target.value)}
              title="Date fin"
            />
          </div>

          {isFilterActive && (
            <button
              className="btn btn-outline btn-sm"
              style={{ borderColor: 'var(--danger)', color: 'var(--danger)' }}
              onClick={resetFilters}
              title="Réinitialiser"
            >
              <RotateCcw size={12} />
              Reset
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="card">{[...Array(3)].map((_, i) => <div key={i} className="skeleton" style={{ height: 48, marginBottom: 8 }} />)}</div>
      ) : filteredInvestments.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">💎</div>
          <h3>{t.investments.noInvestments}</h3>
        </div>
      ) : (
        <div className="table-container">
          <table className="table">
            <thead>
              <tr>
                <th>{t.investments.campagne}</th>
                <th>{t.investments.montant}</th>
                <th>{t.investments.pourcentage}</th>
                <th>{t.investments.date}</th>
                <th>{t.investments.notes}</th>
                <th>{t.common.actions}</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvestments.map(inv => (
                <tr key={inv.id}>
                  <td><strong>{inv.campagne_libelle || inv.campagne_id}</strong></td>
                  <td className="number-format">
                    <span style={{ color: 'var(--gold)', fontWeight: 700 }}>{formatFCFA(inv.montant)}</span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 120 }}>
                      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${inv.pourcentage || 0}%`, height: '100%', background: 'linear-gradient(90deg, var(--gold), var(--gold-dark))', borderRadius: 3 }} />
                      </div>
                      <span style={{ fontWeight: 700, color: 'var(--gold)', minWidth: 42 }} className="number-format">
                        {inv.pourcentage || 0}%
                      </span>
                    </div>
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                    {inv.date ? new Date(inv.date).toLocaleDateString('fr-FR') : '-'}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: '0.875rem', maxWidth: 150 }} className="truncate">
                    {inv.notes || '—'}
                  </td>
                  <td>
                    {isInvestor && (
                      <button className="btn btn-danger btn-sm" onClick={() => setDeleteId(inv.id)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td style={{ fontWeight: 700 }}>Total</td>
                <td className="number-format" style={{ fontWeight: 800, color: 'var(--gold)' }} colSpan={5}>
                  {formatFCFA(totalFCFA)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {showModal && (
        <InvestmentModal onClose={() => setShowModal(false)} onSave={handleSave} t={t} token={token} activeCompany={activeCompany} />
      )}

      <ConfirmModal
        isOpen={!!deleteId}
        title="Supprimer cet investissement"
        message="Êtes-vous sûr de vouloir supprimer cet investissement ?"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
